import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ThresholdSlider } from '@/components/cameras/detection/ThresholdSlider'
import { useGlobalThresholds } from '@/components/detection-accuracy/useGlobalThresholds'
import { SpeciesCatalogManager } from '@/components/detection-accuracy/SpeciesCatalogManager'
import { systemSettingsApi, type BoxLabelMode, type FrameSamplingConfig, type EventVoteTiebreakConfig } from '@/api/endpoints'
import { cn } from '@/lib/utils'
import type { GlobalThresholdModel } from '@/types'

// #118 — super-admin toggle for what text is drawn on bounding boxes in processed media.
function BoxLabelModeControl() {
  const { t } = useTranslation('detectionAccuracy')
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['box-label-mode'],
    queryFn: systemSettingsApi.getBoxLabelMode,
  })
  const mut = useMutation({
    mutationFn: systemSettingsApi.setBoxLabelMode,
    onSuccess: (d) => qc.setQueryData(['box-label-mode'], d),
  })
  const mode = data?.mode ?? 'none'
  const opts: { v: BoxLabelMode; label: string }[] = [
    { v: 'none', label: t('boxLabel.none') },
    { v: 'name', label: t('boxLabel.name') },
    { v: 'name_conf', label: t('boxLabel.nameConf') },
  ]
  return (
    <div className="rounded-lg border border-border p-4">
      <h2 className="text-sm font-semibold">{t('boxLabel.title')}</h2>
      <p className="mb-2 text-xs text-muted-foreground">{t('boxLabel.hint')}</p>
      <div className="flex flex-wrap gap-2">
        {opts.map((o) => (
          <button key={o.v} type="button" disabled={isLoading || mut.isPending}
            onClick={() => mut.mutate(o.v)}
            className={cn('rounded-md border px-3 py-1.5 text-sm transition-colors disabled:opacity-50',
              mode === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted')}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// #143 — super-admin live control of how densely videos are sampled for AI detection.
// Mirrors the bounds the API enforces; commit on blur so a half-typed number is never sent.
const FPS_MIN = 0.1
const FPS_MAX = 30
const FPS_DEFAULT = 3
const MAX_FRAMES_MIN = 1
const MAX_FRAMES_MAX = 256
const MAX_FRAMES_DEFAULT = 48

function FrameSamplingControl() {
  const { t } = useTranslation('detectionAccuracy')
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['frame-sampling'],
    queryFn: systemSettingsApi.getFrameSamplingConfig,
  })
  const mut = useMutation({
    mutationFn: systemSettingsApi.setFrameSamplingConfig,
    onSuccess: (d: FrameSamplingConfig) => qc.setQueryData(['frame-sampling'], d),
  })

  const [fps, setFps] = useState('')
  const [maxFrames, setMaxFrames] = useState('')

  // Server value is the source of truth; drafts only exist between keystroke and blur.
  useEffect(() => {
    if (!data) return
    setFps(String(data.target_fps))
    setMaxFrames(String(data.max_detection_frames))
  }, [data])

  const commit = (
    field: 'target_fps' | 'max_detection_frames',
    raw: string,
    min: number,
    max: number,
    whole: boolean,
  ) => {
    const current = data?.[field]
    const parsed = Number(raw)
    if (raw.trim() === '' || Number.isNaN(parsed)) {
      if (current !== undefined) {
        // Revert the draft; never send garbage.
        if (field === 'target_fps') setFps(String(current))
        else setMaxFrames(String(current))
      }
      return
    }
    const clamped = Math.min(max, Math.max(min, whole ? Math.round(parsed) : parsed))
    if (field === 'target_fps') setFps(String(clamped))
    else setMaxFrames(String(clamped))
    if (clamped === current) return
    mut.mutate({ [field]: clamped })
  }

  const reset = (field: 'target_fps' | 'max_detection_frames') => mut.mutate({ [field]: null })

  const disabled = isLoading || mut.isPending
  const inputCls =
    'w-24 rounded-md border border-border bg-background px-2 py-1 text-sm disabled:opacity-50'

  return (
    <div className="rounded-lg border border-border p-4">
      <h2 className="text-sm font-semibold">{t('frameSampling.title')}</h2>
      <p className="mb-3 text-xs text-muted-foreground">{t('frameSampling.hint')}</p>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex-1 text-sm" htmlFor="fs-fps">
            {t('frameSampling.fps')}
            <span className="ml-2 text-xs text-muted-foreground">
              {t('frameSampling.fpsHint', { min: FPS_MIN, max: FPS_MAX, default: FPS_DEFAULT })}
            </span>
          </label>
          <input id="fs-fps" type="number" inputMode="decimal" step="0.1"
            min={FPS_MIN} max={FPS_MAX} className={inputCls} disabled={disabled}
            value={fps}
            onChange={(e) => setFps(e.target.value)}
            onBlur={(e) => commit('target_fps', e.target.value, FPS_MIN, FPS_MAX, false)} />
          <button type="button" disabled={disabled}
            onClick={() => reset('target_fps')}
            className="text-xs text-primary underline disabled:opacity-40">
            {t('frameSampling.reset')}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex-1 text-sm" htmlFor="fs-max">
            {t('frameSampling.maxFrames')}
            <span className="ml-2 text-xs text-muted-foreground">
              {t('frameSampling.maxFramesHint', {
                min: MAX_FRAMES_MIN, max: MAX_FRAMES_MAX, default: MAX_FRAMES_DEFAULT,
              })}
            </span>
          </label>
          <input id="fs-max" type="number" inputMode="numeric" step="1"
            min={MAX_FRAMES_MIN} max={MAX_FRAMES_MAX} className={inputCls} disabled={disabled}
            value={maxFrames}
            onChange={(e) => setMaxFrames(e.target.value)}
            onBlur={(e) =>
              commit('max_detection_frames', e.target.value,
                MAX_FRAMES_MIN, MAX_FRAMES_MAX, true)} />
          <button type="button" disabled={disabled}
            onClick={() => reset('max_detection_frames')}
            className="text-xs text-primary underline disabled:opacity-40">
            {t('frameSampling.reset')}
          </button>
        </div>
      </div>

      <div className="mt-2 flex h-5 items-center text-xs text-muted-foreground">
        {mut.isPending
          ? t('frameSampling.saving')
          : mut.isError
            ? <span className="text-amber-600">{t('frameSampling.saveError')}</span>
            : mut.isSuccess ? t('frameSampling.saved') : null}
      </div>
    </div>
  )
}

// #155 — super-admin live control of the event-headline tie-break.
// The ceiling is a correctness guard, not a UI nicety: raised far enough this stops
// settling NEAR ties and becomes blanket frame-weighting, which regressed a known event
// in the 18-event replay. The API enforces the same bounds independently.
const MARGIN_MIN = 0
const MARGIN_MAX = 0.2

function VoteTiebreakControl() {
  const { t } = useTranslation('detectionAccuracy')
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['event-vote-tiebreak'],
    queryFn: systemSettingsApi.getEventVoteTiebreak,
  })
  const mut = useMutation({
    mutationFn: systemSettingsApi.setEventVoteTiebreak,
    onSuccess: (d: EventVoteTiebreakConfig) => qc.setQueryData(['event-vote-tiebreak'], d),
  })

  const [margin, setMargin] = useState('')

  useEffect(() => {
    if (data) setMargin(String(data.margin))
  }, [data])

  // Commit on blur so a half-typed number is never sent, and clamp locally so the field
  // shows what was actually stored rather than what was typed.
  const commit = (raw: string) => {
    const parsed = Number(raw)
    if (raw.trim() === '' || Number.isNaN(parsed)) {
      if (data) setMargin(String(data.margin))
      return
    }
    const clamped = Math.min(MARGIN_MAX, Math.max(MARGIN_MIN, parsed))
    setMargin(String(clamped))
    if (data && clamped === data.margin) return
    mut.mutate({ margin: clamped })
  }

  const disabled = isLoading || mut.isPending

  return (
    <div className="rounded-lg border border-border p-4">
      <h2 className="text-sm font-semibold">{t('voteTiebreak.title')}</h2>
      <p className="mb-3 text-xs text-muted-foreground">{t('voteTiebreak.hint')}</p>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex-1 text-sm" htmlFor="vt-margin">
          {t('voteTiebreak.margin')}
          <span className="ml-2 text-xs text-muted-foreground">
            {t('voteTiebreak.marginHint', { min: MARGIN_MIN, max: MARGIN_MAX })}
          </span>
        </label>
        <input id="vt-margin" type="number" inputMode="decimal" step="0.01"
          min={MARGIN_MIN} max={MARGIN_MAX}
          className="w-24 rounded-md border border-border bg-background px-2 py-1 text-sm disabled:opacity-50"
          disabled={disabled}
          value={margin}
          onChange={(e) => setMargin(e.target.value)}
          onBlur={(e) => commit(e.target.value)} />
        <button type="button" disabled={disabled}
          onClick={() => mut.mutate({ margin: null })}
          className="text-xs text-primary underline disabled:opacity-40">
          {t('voteTiebreak.reset')}
        </button>
      </div>

      <div className="mt-2 flex h-5 items-center text-xs text-muted-foreground">
        {mut.isPending
          ? t('voteTiebreak.saving')
          : mut.isError
            ? <span className="text-amber-600">{t('voteTiebreak.saveError')}</span>
            : mut.isSuccess ? t('voteTiebreak.saved') : null}
      </div>
    </div>
  )
}

function ModelBlock({ model, onCommit, baseDefault }: {
  model: GlobalThresholdModel
  baseDefault: number
  onCommit: (classId: number, conf: number | null) => void
}) {
  const { t } = useTranslation('detectionAccuracy')
  const [openModel, setOpenModel] = useState(true)
  return (
    <div className="rounded-lg border border-border">
      <button type="button" onClick={() => setOpenModel((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="font-medium">{model.ai_model_name}</span>
        <span className="text-xs text-muted-foreground">
          {model.classes.length} {t('classes')} · {t('base')} {Math.round(baseDefault * 100)}%
        </span>
      </button>
      {openModel && (
        <div className="space-y-3 border-t border-border px-4 py-3">
          {model.classes.map((c) => {
            const inherit = c.global_confidence === null
            const value = c.global_confidence ?? baseDefault
            return (
              <div key={c.class_id} className="grid grid-cols-[10rem_1fr_auto] items-center gap-3">
                <span className="truncate text-sm" title={c.class_name}>{c.class_name}</span>
                <ThresholdSlider
                  value={value}
                  ariaLabel={`${model.ai_model_name} ${c.class_name}`}
                  onCommit={(v) => onCommit(c.class_id, v)}
                />
                <button type="button"
                  disabled={inherit}
                  onClick={() => onCommit(c.class_id, null)}
                  className="text-xs text-primary underline disabled:opacity-40">
                  {inherit ? `${t('base')} ${Math.round(baseDefault * 100)}%` : t('reset')}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function DetectionAccuracyPage() {
  const { t } = useTranslation('detectionAccuracy')
  const g = useGlobalThresholds()
  const speciesModel = g.models.find((m) => m.base_model_type === 'speciesnet-bioclip')

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      {/* #126 — nav bar shows "Detection Accuracy"; drop the redundant title/subtitle. */}
      <div className="flex items-center justify-end">
        <button type="button"
          onClick={() => { if (window.confirm(t('resetAllConfirm'))) g.resetCameras() }}
          disabled={g.resetting}
          className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
          {t('resetAll')}
        </button>
      </div>

      <div className="flex h-5 items-center text-xs text-muted-foreground">
        {g.saving ? t('saving') : g.saveError ? <span className="text-amber-600">{t('saveRetry')}</span> : t('saved')}
      </div>

      {g.isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('loading')}
        </div>
      ) : g.isError ? (
        <div className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
          <span>{t('loadError')}</span>
          <button type="button" onClick={() => g.refetch()} className="text-primary underline">{t('retry')}</button>
        </div>
      ) : g.models.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">{t('noModels')}</p>
      ) : (
        <div className="space-y-3">
          {g.models.map((m) => (
            <ModelBlock key={m.ai_model_id} model={m} baseDefault={m.base_default}
              onCommit={(class_id, confidence) =>
                g.update({ base_model_id: m.base_model_id, class_id, confidence })} />
          ))}
        </div>
      )}

      <BoxLabelModeControl />

      <FrameSamplingControl />

      <VoteTiebreakControl />

      {speciesModel && <SpeciesCatalogManager baseModelId={speciesModel.base_model_id} />}
    </div>
  )
}

export default DetectionAccuracyPage
